import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Storefront, Copy, DownloadSimple, ArrowSquareOut, ShieldStar, ListBullets } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { BrutalButton } from "../components/Brutal";
import { composeCardPng, QrSaveDialog } from "../components/CodeCardDialog";
import { PriceListDialog } from "../components/PriceListDialog";
import { DiscountCodesCard } from "../components/DiscountCodesCard";
import { ShareDocsCard } from "../components/ShareDocsCard";
import { StoreMomentumCard } from "../components/StoreMomentumCard";
import { VideoViewsCard } from "../components/VideoViewsCard";
import { copyToClipboard } from "../lib/clipboard";

const CATEGORY_ORDER = ["Injectable", "Precision Nasal Mist", "Flagship Topical"];
const money = (v) => (v == null ? "—" : `$${Number(v) % 1 === 0 ? Number(v) : Number(v).toFixed(2)}`);

// v405.15 "My Store" — every code-holder manages their own retail prices
// (floor = their own wholesale) and grabs their store link / branded QR.
// v406.1 — `?as={user_id}` puts owner/admin/cfo (and partners, for their
// subtree) in GOD MODE on any distributor's store.
export default function MyStorePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const asId = params.get("as");
  const asParams = asId ? { as: asId } : {};
  const qrRef = useRef(null);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({}); // product_id -> input string
  const [savingId, setSavingId] = useState(null);
  const [qrPreview, setQrPreview] = useState(null); // v408.5 iOS-friendly QR dialog
  const [discDraft, setDiscDraft] = useState(null); // v409.0 storewide discount slider
  const [savingDisc, setSavingDisc] = useState(false);
  const [payDraft, setPayDraft] = useState(null); // v413.2 payment instructions
  const [savingPay, setSavingPay] = useState(false);
  const [savingConcierge, setSavingConcierge] = useState(false); // v414.33
  const [showPriceList, setShowPriceList] = useState(false); // v414.59

  const load = () =>
    api.get("/my-store", { params: asParams })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { setDrafts({}); setDiscDraft(null); setPayDraft(null); setData(null); load(); /* eslint-disable-next-line */ }, [asId]);

  if (!data) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="my-store-loading">{t("common.loading")}</div>;
  }

  const storeUrl = `${window.location.origin}${data.store_path}`;
  const savedDisc = data.store_discount_pct || 0;
  const disc = discDraft !== null ? discDraft : savedDisc;
  const payText = payDraft !== null ? payDraft : (data.payment_instructions || "");

  const savePayInstr = async () => {
    setSavingPay(true);
    try {
      await api.put("/my-store/payment-instructions", { text: payText }, { params: asParams });
      toast.success(payText.trim() ? t("my_store.payinstr_saved") : t("my_store.payinstr_cleared"));
      setPayDraft(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPay(false);
    }
  };

  // v414.33 — optional $5/vial concierge add-on at checkout (pickup only)
  const toggleConcierge = async (enabled) => {
    setSavingConcierge(true);
    try {
      await api.put("/my-store/concierge", { enabled }, { params: asParams });
      setData({ ...data, concierge_enabled: enabled });
      toast.success(enabled ? t("my_store.concierge_on") : t("my_store.concierge_off"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingConcierge(false);
    }
  };

  const saveDiscount = async () => {
    setSavingDisc(true);
    try {
      await api.put("/my-store/discount", { pct: disc }, { params: asParams });
      toast.success(disc > 0
        ? t("my_store.discount_saved", { pct: disc })
        : t("my_store.discount_cleared"));
      setDiscDraft(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDisc(false);
    }
  };

  const save = async (p, raw) => {
    const trimmed = (raw ?? "").trim();
    const value = trimmed === "" ? null : Number(trimmed);
    if (value !== null && (!Number.isFinite(value) || value <= 0)) {
      toast.error(t("my_store.invalid_price"));
      return;
    }
    setSavingId(p.product_id);
    try {
      await api.put(`/my-store/prices/${p.product_id}`, { retail_price: value }, { params: asParams });
      toast.success(value === null
        ? t("my_store.price_cleared", { name: p.name })
        : t("my_store.price_saved", { name: p.name, price: money(value) }));
      setDrafts((d) => { const { [p.product_id]: _, ...rest } = d; return rest; });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const downloadQr = () => {
    // v408.5 — iPhone can't receive silent downloads; show the card in a
    // dialog instead (long-press to save / native share sheet).
    const png = composeCardPng(qrRef.current?.querySelector("canvas"), {
      name: data.managing ? data.managing.name : (user?.name || data.code),
      code: data.code,
      heading: t("code_card.store_heading"),
      instruction: t("code_card.store_instruction"),
      url: storeUrl,
      tagline: t("code_card.tagline_default"),
    });
    setQrPreview(png);
  };

  const groups = CATEGORY_ORDER
    .map((cat) => ({ cat, items: data.products.filter((p) => p.category === cat) }))
    .concat([{ cat: "__other", items: data.products.filter((p) => !CATEGORY_ORDER.includes(p.category)) }])
    .filter((g) => g.items.length > 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="my-store-page">
      {/* hidden QR canvas used to compose the downloadable card */}
      <div ref={qrRef} className="hidden">
        <QRCodeCanvas value={storeUrl} size={480} fgColor="#09090b" level="M" includeMargin />
      </div>

      {/* v408.5 — QR card preview (long-press to save on iPhone) */}
      {qrPreview && (
        <QrSaveDialog
          png={qrPreview}
          filename={`${data.code}-store-qr.png`}
          url={storeUrl}
          onClose={() => setQrPreview(null)}
        />
      )}

      {/* v414.59 — shareable live price list (image / PDF / text) */}
      {showPriceList && (
        <PriceListDialog
          code={data.code}
          storeName={data.managing ? data.managing.name : (user?.name || data.code)}
          onClose={() => setShowPriceList(false)}
        />
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Storefront size={24} weight="bold" className="text-[#8a6a1c]" />{" "}
            {data.managing ? t("my_store.managing_title", { name: data.managing.name }) : t("my_store.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-xl">{t("my_store.subtitle")}</p>
        </div>
      </div>

      {data.managing && (
        <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3" data-testid="my-store-managing-banner">
          <ShieldStar size={18} weight="bold" className="text-violet-600 mt-0.5 shrink-0" />
          <p className="text-xs text-violet-800">
            {t("my_store.managing_note", { name: data.managing.name, code: data.managing.code })}
          </p>
        </div>
      )}

      {/* Store link + QR card */}
      <div className="bg-white border-2 border-[#c9a24a] rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="my-store-link-card">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{t("my_store.your_link")}</p>
          <p className="font-mono text-sm text-[#8a6a1c] truncate" data-testid="my-store-url">{storeUrl.replace(/^https?:\/\//, "")}</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <BrutalButton size="sm" variant="secondary" onClick={() => copyToClipboard(storeUrl, t("my_store.link_copied"))} data-testid="my-store-copy-link">
            <Copy size={14} weight="bold" /> {t("my_store.copy")}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" onClick={() => window.open(storeUrl, "_blank")} data-testid="my-store-open-link">
            <ArrowSquareOut size={14} weight="bold" /> {t("my_store.view")}
          </BrutalButton>
          <BrutalButton size="sm" onClick={downloadQr} data-testid="my-store-download-qr">
            <DownloadSimple size={14} weight="bold" /> {t("my_store.download_qr")}
          </BrutalButton>
          <BrutalButton size="sm" onClick={() => setShowPriceList(true)} data-testid="my-store-price-list-btn">
            <ListBullets size={14} weight="bold" /> {t("my_store.price_list")}
          </BrutalButton>
        </div>
      </div>

      {/* v409.1 — costs hidden until the parent sets this sub's margin */}
      {data.pricing_pending && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-800" data-testid="my-store-pricing-pending-banner">
          {t("my_store.pricing_pending_note")}
        </div>
      )}

      {/* v414.25 — storefront video play counts */}
      <VideoViewsCard asParams={asParams} />

      {/* v409.0 — storewide discount slider (5% steps, floor-clamped) */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="my-store-discount-card">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-900">{t("my_store.discount_title")}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{t("my_store.discount_hint")}</p>
          </div>
          <span
            className={`shrink-0 text-xl font-bold tabular-nums ${disc > 0 ? "text-emerald-700" : "text-zinc-300"}`}
            data-testid="my-store-discount-value"
          >
            {disc > 0 ? `-${disc}%` : t("my_store.discount_off")}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="50"
          step="5"
          value={disc}
          onChange={(e) => setDiscDraft(Number(e.target.value))}
          className="w-full mt-3 accent-[#8a6a1c] cursor-pointer"
          data-testid="my-store-discount-slider"
        />
        <div className="flex justify-between text-[10px] text-zinc-400 tabular-nums">
          <span>0%</span><span>25%</span><span>50%</span>
        </div>
        {discDraft !== null && discDraft !== savedDisc && (
          <BrutalButton
            size="sm"
            className="mt-3 w-full"
            disabled={savingDisc}
            onClick={saveDiscount}
            data-testid="my-store-discount-save"
          >
            {savingDisc ? "…" : t("my_store.discount_apply", { pct: disc })}
          </BrutalButton>
        )}
      </div>

      {/* v414.0 — store momentum */}
      <StoreMomentumCard key={`m-${asId || "self"}`} asParams={asParams} />

      {/* v413.2 — payment instructions for "arrange payment" customers */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="my-store-payinstr-card">
        <p className="text-sm font-semibold text-zinc-900">{t("my_store.payinstr_title")}</p>
        <p className="text-xs text-zinc-500 mt-0.5">
          {t("my_store.payinstr_hint")}
          {data.currency && data.currency !== "CAD" && (
            <span className="font-semibold text-[#8a6a1c]"> {t("my_store.payinstr_intl", { currency: data.currency })}</span>
          )}
        </p>
        <textarea
          value={payText}
          onChange={(e) => setPayDraft(e.target.value)}
          rows={2}
          maxLength={400}
          placeholder={t("my_store.payinstr_ph")}
          className="w-full mt-2 px-3 py-2 rounded-lg border border-zinc-300 text-sm font-medium resize-none focus:outline-none focus:border-[#C9A84C]"
          data-testid="my-store-payinstr-input"
        />
        {payDraft !== null && payDraft !== (data.payment_instructions || "") && (
          <BrutalButton size="sm" onClick={savePayInstr} disabled={savingPay} className="mt-2" data-testid="my-store-payinstr-save">
            {savingPay ? "…" : t("my_store.payinstr_save")}
          </BrutalButton>
        )}
      </div>

      {/* v414.33 — concierge service toggle */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="my-store-concierge-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-900">{t("my_store.concierge_title")}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{t("my_store.concierge_hint")}</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!data.concierge_enabled}
            disabled={savingConcierge}
            onClick={() => toggleConcierge(!data.concierge_enabled)}
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${data.concierge_enabled ? "bg-[#c9a24a]" : "bg-zinc-300"}`}
            data-testid="my-store-concierge-toggle"
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${data.concierge_enabled ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
      </div>

      {/* v413.0 — one-time discount codes */}
      <DiscountCodesCard asParams={asParams} />

      {/* v414.5 — shareable customer documents + LIA pitch (+videos v414.84) */}
      <ShareDocsCard storeCode={data?.code} />

      {/* Price manager */}
      {groups.map(({ cat, items }) => (
        <section key={cat} data-testid={`my-store-section-${cat}`}>
          <h2 className="text-[12px] font-bold uppercase tracking-[0.25em] text-[#8a6a1c] border-b-2 border-[#c9a24a] pb-1.5 mb-1">
            {t(`my_store.cat_${cat}`, { defaultValue: cat === "__other" ? t("my_store.cat_other") : cat })}
          </h2>
          <ul className="divide-y divide-zinc-100">
            {items.map((p) => {
              const draft = drafts[p.product_id];
              const dirty = draft !== undefined && draft !== (p.my_retail != null ? String(p.my_retail) : "");
              return (
                <li key={p.product_id} className="py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`my-store-row-${p.product_id}`}>
                  <div className="min-w-0 flex-1 basis-52">
                    <p className="text-sm font-semibold text-zinc-900 truncate">{p.name}</p>
                    <p className="text-[11px] text-zinc-500">
                      {t("my_store.your_cost")}: <b className="text-zinc-700">{money(p.my_ws)}</b>
                      <span className="mx-1.5 text-zinc-300">·</span>
                      {t("my_store.standard")}: {money(p.standard_retail)}
                      {p.discount_applied && (
                        <>
                          <span className="mx-1.5 text-zinc-300">·</span>
                          <span className="text-emerald-700 font-semibold" data-testid={`my-store-discounted-${p.product_id}`}>
                            -{savedDisc}% → {money(p.effective_price)}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={p.standard_retail != null ? String(p.standard_retail) : ""}
                        value={draft !== undefined ? draft : (p.my_retail != null ? p.my_retail : "")}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.product_id]: e.target.value }))}
                        className="input-brutal h-10 !py-0 pl-6 w-28 text-right font-semibold tabular-nums"
                        data-testid={`my-store-price-input-${p.product_id}`}
                      />
                    </div>
                    {dirty && (
                      <BrutalButton
                        size="sm"
                        disabled={savingId === p.product_id}
                        onClick={() => save(p, draft)}
                        data-testid={`my-store-save-${p.product_id}`}
                      >
                        {savingId === p.product_id ? "…" : t("my_store.save")}
                      </BrutalButton>
                    )}
                    {!dirty && p.my_retail != null && (
                      <button
                        type="button"
                        onClick={() => save(p, "")}
                        className="text-[11px] font-semibold text-zinc-400 hover:text-red-600 transition-colors"
                        data-testid={`my-store-clear-${p.product_id}`}
                      >
                        {t("my_store.reset")}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <p className="text-xs text-zinc-400">{t("my_store.floor_note")}</p>
    </div>
  );
}
